import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { submitAppeal } from './ReportHandler';
import { auth } from '../../firebase';
import './ReportModal.css'; // Reuse the same CSS
import { toast } from 'react-hot-toast';

const AppealModal = ({ 
  contentId, 
  contentType, 
  removedText, 
  queryId = null, 
  onClose 
}) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  
  // Validate that we have required params
  useEffect(() => {
    if (!contentId || !contentType) {
      setError('Missing required information to submit appeal. Please try again later.');
    } else {
      setError('');
    }
  }, [contentId, contentType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!auth.currentUser) {
      toast.error('You must be logged in to submit an appeal');
      return;
    }

    if (reason.trim() === '') {
      toast.error('Please explain why you believe this content should be restored');
      return;
    }
    
    if (!contentId || !contentType) {
      toast.error('Missing required information to submit appeal');
      setError('Missing required information to submit appeal. Please try again later.');
      return;
    }

    setLoading(true);
    
    try {
      console.log(`Submitting appeal for ${contentType} with ID ${contentId}`);
      const result = await submitAppeal(
        contentId,
        contentType,
        auth.currentUser.uid,
        reason,
        queryId
      );

      if (result.success) {
        setSubmitted(true);
        toast.success('Your appeal has been submitted for review');
      } else {
        toast.error(`Error submitting appeal: ${result.error}`);
        setError(result.error || 'Failed to submit appeal');
      }
    } catch (error) {
      console.error('Error submitting appeal:', error);
      toast.error('Something went wrong. Please try again.');
      setError(error.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="report-modal-overlay">
      <div className="report-modal">
        <div className="report-modal-header">
          <h2>Appeal Content Removal</h2>
          <button className="close-button" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>
        
        <div className="report-modal-content">
          {error ? (
            <div className="report-error">
              <h3>Error</h3>
              <p className="error-message">{error}</p>
              <button 
                className="close-btn" 
                onClick={onClose}
              >
                Close
              </button>
            </div>
          ) : submitted ? (
            <div className="report-success">
              <h3>Appeal Submitted</h3>
              <p className="success-message">
                Thank you for your appeal. Our team will review your request and make a decision.
              </p>
              <p className="success-message">
                You will be notified once your appeal has been reviewed.
              </p>
              <button 
                className="close-btn" 
                onClick={onClose}
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <p className="appeal-description">
                  Your content was removed because it was flagged for violating our community guidelines. 
                  If you believe this was a mistake, please provide details below explaining why your content should be restored.
                </p>
                
                <label htmlFor="appealReason">Why should this content be restored?</label>
                <textarea
                  id="appealReason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Explain why you believe this content should be restored..."
                  rows={5}
                  required
                />
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Submit Appeal'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppealModal; 